import type { ReactNode, SVGProps, InputHTMLAttributes, SelectHTMLAttributes, MouseEvent as ReactMouseEvent } from 'react'
import { useState } from 'react'
import { Spin } from 'antd'
import { cn } from '@/utils/cn'
import { caseStatusKeyByLabel } from '@/constants/enums'
import { PAGINATION } from '@/constants'
import type { SortState, SortOrder } from './listHelpers'

/* ------------------------------------------------------------------ */
/*  Pagination                                                        */
/* ------------------------------------------------------------------ */

export interface PaginationProps {
  currentPage: number
  totalPages: number
  total: number
  rangeFrom: number
  rangeTo: number
  onPageChange: (page: number) => void
  className?: string
  pageSize?: number
  onPageSizeChange?: (size: number) => void
}

function PageSizeInput({ pageSize, onPageSizeChange }: { pageSize: number; onPageSizeChange: (size: number) => void }) {
  const [draft, setDraft] = useState(String(pageSize))

  const commit = () => {
    const parsed = Number(draft)
    if (!Number.isFinite(parsed) || parsed < 1) {
      setDraft(String(pageSize))
      return
    }
    const next = Math.min(PAGINATION.MAX_SIZE, Math.floor(parsed))
    setDraft(String(next))
    if (next !== pageSize) onPageSizeChange(next)
  }

  return (
    <span className="ml-3 inline-flex items-center gap-1 text-sm text-gray-500">
      表示件数
      <input
        type="number"
        min={1}
        max={PAGINATION.MAX_SIZE}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            ;(e.target as HTMLInputElement).blur()
          }
        }}
        className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
      件
    </span>
  )
}

const buildPages = (currentPage: number, totalPages: number): Array<number | '...'> => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1)

  const pages: Array<number | '...'> = []
  const add = (value: number | '...') => {
    if (pages.length > 0 && pages[pages.length - 1] === '...' && value === '...') return
    pages.push(value)
  }

  add(1)
  if (currentPage > 3) add('...')
  for (let page = Math.max(2, currentPage - 1); page <= Math.min(totalPages - 1, currentPage + 1); page += 1) {
    add(page)
  }
  if (currentPage < totalPages - 2) add('...')
  if (totalPages > 1) add(totalPages)

  return pages
}

export function Pagination({
  currentPage,
  totalPages,
  total,
  rangeFrom,
  rangeTo,
  onPageChange,
  className,
  pageSize,
  onPageSizeChange,
}: PaginationProps) {
  return (
    <div className={cn('mt-4 flex flex-wrap items-center justify-between gap-3', className)}>
      <div className="flex flex-wrap items-center">
        <p className="text-sm text-gray-500">{total === 0 ? '0 件 / 全 0 件' : `${rangeFrom} - ${rangeTo} 件 / 全 ${total} 件`}</p>
        {pageSize !== undefined && onPageSizeChange && (
          <PageSizeInput pageSize={pageSize} onPageSizeChange={onPageSizeChange} />
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          前へ
        </button>
        {buildPages(currentPage, totalPages).map((page, index) =>
          page === '...' ? (
            <span key={`ellipsis-${index}`} className="select-none px-2 text-gray-400">
              ...
            </span>
          ) : (
            <button
              key={page}
              type="button"
              onClick={() => onPageChange(page)}
              className={cn(
                'min-w-[2.25rem] cursor-pointer rounded-lg px-2 py-1.5 text-sm',
                currentPage === page
                  ? 'bg-blue-600 font-medium text-white'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
              )}
            >
              {page}
            </button>
          ),
        )}
        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          次へ
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Sort                                                              */
/* ------------------------------------------------------------------ */

function nextSortOrder(field: string, current: SortState): SortState {
  if (current.sortBy !== field) return { sortBy: field, order: 'asc' }
  if (current.order === 'asc') return { sortBy: field, order: 'desc' }
  return { sortBy: null, order: null }
}

export interface SortableHeaderProps {
  field: string
  label: ReactNode
  sort: SortState
  onSortChange: (next: SortState) => void
  className?: string
  trailing?: ReactNode
}

export function SortableHeader({ field, label, sort, onSortChange, className, trailing }: SortableHeaderProps) {
  const active = sort.sortBy === field ? sort.order : null
  return (
    <th
      scope="col"
      className={cn(
        'relative cursor-pointer select-none px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500',
        'hover:bg-gray-100',
        className,
      )}
      onClick={() => onSortChange(nextSortOrder(field, sort))}
      aria-sort={active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : 'none'}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <SortIcon active={active} />
      </span>
      {trailing}
    </th>
  )
}

function SortIcon({ active }: { active: SortOrder }) {
  return (
    <span className="inline-flex flex-col leading-none gap-[1px]" aria-hidden="true">
      <svg
        className={cn('h-1.5 w-1.5', active === 'asc' ? 'text-blue-600' : 'text-gray-300')}
        viewBox="0 0 8 5"
        fill="currentColor"
      >
        <path d="M4 0L8 5H0z" />
      </svg>
      <svg
        className={cn('h-1.5 w-1.5', active === 'desc' ? 'text-blue-600' : 'text-gray-300')}
        viewBox="0 0 8 5"
        fill="currentColor"
      >
        <path d="M4 5L0 0H8z" />
      </svg>
    </span>
  )
}

export function BackButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('mb-4 inline-flex cursor-pointer items-center gap-1 text-sm text-gray-600 hover:text-blue-600', className)}
    >
      <ChevronLeftIcon className="h-4 w-4" />
      戻る
    </button>
  )
}

export function DownloadIcon({ className }: SVGProps<SVGSVGElement>) {
  return (
    <svg className={cn('mr-2 h-4 w-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  )
}

export function PrintIcon({ className }: SVGProps<SVGSVGElement>) {
  return (
    <svg className={cn('mr-2 h-4 w-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
  )
}

export function ArrowRightIcon({ className }: SVGProps<SVGSVGElement>) {
  return (
    <svg className={cn('mr-2 h-4 w-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  )
}

function ChevronLeftIcon({ className }: SVGProps<SVGSVGElement>) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  )
}

export function FileDocIcon({ className }: SVGProps<SVGSVGElement>) {
  return (
    <svg className={cn('mr-2 h-4 w-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  )
}

export function LoadingSpinner({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-flex items-center justify-center text-blue-600', className)}
      role="status"
      aria-live="polite"
      aria-label="読み込み中"
    >
      <svg className="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle className="opacity-25" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" />
        <path className="opacity-90" fill="currentColor" d="M12 3a9 9 0 0 1 9 9h-3a6 6 0 0 0-6-6V3z" />
      </svg>
    </span>
  )
}

export function TableLoadingRow({ colSpan, className }: { colSpan: number; className?: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className={cn('px-4 py-10', className)}>
        <div className="flex items-center justify-center">
          <Spin />
        </div>
      </td>
    </tr>
  )
}

export function CardLoadingState({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center justify-center py-12', className)}>
      <Spin />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Card wrappers                                                     */
/* ------------------------------------------------------------------ */

export function SearchCard({
  title = '検索条件',
  extra,
  children,
  className,
}: {
  title?: string
  extra?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('rounded-xl bg-white p-6 shadow-lg', className)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2 min-w-0">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <span className="text-xs text-gray-500 shrink-0">AND条件</span>
        </div>
        {extra}
      </div>
      {children}
    </div>
  )
}

export function ResultCard({
  title,
  extra,
  subtitle,
  children,
  className,
}: {
  title?: ReactNode
  extra?: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('rounded-xl bg-white p-6 shadow-lg', className)}>
      {(title || extra) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            {title && (typeof title === 'string' ? <h2 className="text-xl font-bold text-gray-800">{title}</h2> : title)}
            {subtitle}
          </div>
          {extra && <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-3">{extra}</div>}
        </div>
      )}
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Form inputs                                                       */
/* ------------------------------------------------------------------ */

export function DemoInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  // 原生 <input type="date"> は max 未指定だと年入力欄が6桁になる（ブラウザ既定の上限 275760 年）。
  // 4桁に収めるため、date 型では呼び出し側が明示しない限り 1900〜9999 年を既定の範囲とする。
  const dateRangeDefaults =
    props.type === 'date' ? { min: props.min ?? '1900-01-01', max: props.max ?? '9999-12-31' } : null
  return (
    <input
      className={cn('rounded-lg border border-gray-300 px-4 py-2', className)}
      {...props}
      {...dateRangeDefaults}
    />
  )
}

const SELECT_CHEVRON_BG =
  "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%236b7280' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")"

export function DemoSelect({ className, style, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        'appearance-none rounded-lg border border-gray-300 bg-white pl-4 pr-10 py-2 text-sm text-gray-700',
        'cursor-pointer transition',
        'hover:border-gray-400',
        'focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20',
        className,
      )}
      style={{
        backgroundImage: SELECT_CHEVRON_BG,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 12px center',
        backgroundSize: '16px 16px',
        ...style,
      }}
      {...props}
    >
      {children}
    </select>
  )
}

/* ------------------------------------------------------------------ */
/*  Status badge                                                      */
/* ------------------------------------------------------------------ */

// 汎用ステータス（受給状態、申出書 has_defect、ロール表示、取込中等）に対する固定色。
// 案件ステータス（CaseStatus）はラベル変更があり得るため、enum key 経由で動的に解決する（下記参照）。
const GENERIC_BADGE_COLORS: Record<string, string> = {
  完了: 'bg-green-100 text-green-800',
  処理中: 'bg-blue-100 text-blue-800',
  取込中: 'bg-blue-100 text-blue-800',
  エラーあり: 'bg-red-100 text-red-800',
  エラー: 'bg-red-100 text-red-800',
  取込エラー: 'bg-red-100 text-red-800',
  確認済: 'bg-emerald-100 text-emerald-900',
  保護受給中: 'bg-green-100 text-green-700',
  保護廃止中: 'bg-gray-100 text-gray-600',
  受給中: 'bg-green-100 text-green-700',
  廃止: 'bg-gray-100 text-gray-600',
  給付済: 'bg-green-100 text-green-700',
  審査中: 'bg-yellow-100 text-yellow-700',
  未給付: 'bg-red-100 text-red-700',
  承認: 'bg-green-100 text-green-700',
  未通知: 'bg-yellow-100 text-yellow-700',
  通知済: 'bg-blue-100 text-blue-700',
  有効: 'bg-green-100 text-green-700',
  無効: 'bg-red-100 text-red-700',
  通知書未出力: 'bg-amber-100 text-amber-900',
  通知書出力済: 'bg-sky-100 text-sky-800',
  口座照会データ出力済: 'bg-indigo-100 text-indigo-800',
  管理者: 'bg-purple-100 text-purple-700',
  一般: 'bg-blue-100 text-blue-700',
  閲覧者: 'bg-gray-100 text-gray-600',
}

// 案件ステータス（CaseStatus）の enum key → Tailwind クラス。
// DemoBadge では i18n 経由でラベル → key 逆引きしてからこの map を引く。
const CASE_STATUS_BADGE_BY_KEY: Record<string, string> = {
  IDENT_ID_UNEXPORTED: 'bg-amber-100 text-amber-900',
  APP_AWAITING_SUBMIT: 'bg-yellow-100 text-yellow-800',
  APP_UNDER_REVIEW: 'bg-yellow-100 text-yellow-700',
  APP_REJECTED: 'bg-red-100 text-red-700',
  AWAITING_TRANSFER_LIST: 'bg-cyan-100 text-cyan-800',
  AWAITING_CALC: 'bg-cyan-100 text-cyan-800',
  CALC_IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
  CALC_ERROR: 'bg-red-100 text-red-800',
  CALC_DONE: 'bg-emerald-100 text-emerald-800',
  // 客先指摘: 作成済と出力済が見分けづらい。出力済を「白字＋ベタ青背景(#4F81BD)」へ変更し
  // 薄色ピル群の中で際立たせたので、作成済は元の薄青(sky)へ戻す。
  NOTICE_PRINTED: 'bg-sky-100 text-sky-800',
  NOTICE_DOWNLOADED: 'bg-[#4F81BD] text-white',
  ZENGIN_PROD_EXPORTED: 'bg-purple-100 text-purple-800',
  COMPLETED_CASH: 'bg-green-100 text-green-800',
  COMPLETED_DENIED: 'bg-stone-200 text-stone-700',
  COMPLETED_PLAINTIFF_CALC: 'bg-stone-200 text-stone-700',
}

function resolveBadgeColor(status: string): string {
  // 先に案件ステータスとして label → enum key 逆引きを試行（ラベル変更に強い動的解決）。
  const caseKey = caseStatusKeyByLabel(status)
  if (caseKey && CASE_STATUS_BADGE_BY_KEY[caseKey]) {
    return CASE_STATUS_BADGE_BY_KEY[caseKey]
  }
  return GENERIC_BADGE_COLORS[status] ?? 'bg-gray-100 text-gray-700'
}

export function DemoBadge({ status, className }: { status: string; className?: string }) {
  const color = resolveBadgeColor(status)
  return (
    <span
      className={cn(
        'inline-flex max-w-full min-w-0 items-center overflow-hidden rounded-full px-2 py-1 text-xs',
        color,
        className,
      )}
    >
      <span className="min-w-0 truncate">{status}</span>
    </span>
  )
}

/* ------------------------------------------------------------------ */
/*  Resizable columns                                                 */
/* ------------------------------------------------------------------ */

export function ResizeHandle({
  columnId,
  onMouseDown,
}: {
  columnId: string
  onMouseDown: (columnId: string, e: ReactMouseEvent) => void
}) {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      onMouseDown={(e) => onMouseDown(columnId, e)}
      onClick={(e) => e.stopPropagation()}
      className="group absolute right-0 top-0 bottom-0 w-2 cursor-col-resize select-none"
    >
      <span className="absolute right-0 top-1/2 -translate-y-1/2 block h-1/2 w-px bg-gray-300 transition-colors group-hover:w-0.5 group-hover:bg-blue-500 group-active:bg-blue-600" />
    </span>
  )
}
